import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon, type IconName } from './Icon'

/**
 * Toasts. Replaces the single notice slot the first pass had, which could only
 * ever show the most recent thing that happened — so a save followed by an
 * error showed the error and silently lost the save.
 */
export type ToastKind = 'info' | 'success' | 'error' | 'warn'

export interface Toast {
  id: number
  kind: ToastKind
  title: string
  text?: string
  action?: { label: string; href?: string; onClick?: () => void }
  /** Errors stay until dismissed; anything else clears itself. */
  sticky?: boolean
  /**
   * How many times this exact thing has happened, when it has happened more
   * than once.
   *
   * A failing action is usually a failing action several times: an autosave
   * that retries, a button pressed again because nothing appeared to happen.
   * Three identical stacked copies of "Could not save" say nothing the first
   * one did not, and they push everything else off the screen — the owner met
   * exactly that, three copies of one refusal, on the first afternoon of use.
   */
  seen?: number
}

const ICON: Record<ToastKind, IconName> = {
  info: 'info',
  success: 'check',
  error: 'alert',
  warn: 'alert',
}

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([])
  const seq = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = ++seq.current
    setToasts((list) => {
      /*
       * The same thing again is the same toast, counted.
       *
       * Same kind, same title, same words — anything else is a different
       * event and gets a row of its own. The MOST RECENT one is the one that
       * repeats, because a repeat that appeared under three newer messages
       * would be a count nobody sees change; and it keeps its id, so a sticky
       * one somebody has already read does not jump back to the bottom.
       */
      const last = list[list.length - 1]
      if (
        last &&
        last.kind === toast.kind &&
        last.title === toast.title &&
        last.text === toast.text
      ) {
        return [...list.slice(0, -1), { ...last, seen: (last.seen ?? 1) + 1 }]
      }
      return [...list.slice(-3), { ...toast, id }]
    })
    return id
  }, [])

  return {
    toasts,
    dismiss,
    info: (title: string, text?: string) =>
      push({ kind: 'info', title, ...(text ? { text } : {}) }),
    success: (title: string, text?: string, action?: Toast['action']) =>
      push({ kind: 'success', title, ...(text ? { text } : {}), ...(action ? { action } : {}) }),
    /**
     * `extra` for the warning somebody has to ACT on.
     *
     * Most warnings are a fact about what just happened — "staged, but not in
     * the navigation" — and clearing themselves is right. A few are a job:
     * the last recovery code is spent, and until two-step sign-in is set up
     * again a lost authenticator locks the account out. A sentence like that
     * disappearing after four seconds is the product mentioning it.
     */
    warn: (title: string, text?: string, extra?: { action?: Toast['action']; sticky?: boolean }) =>
      push({
        kind: 'warn',
        title,
        ...(text ? { text } : {}),
        ...(extra?.action ? { action: extra.action } : {}),
        ...(extra?.sticky ? { sticky: true } : {}),
      }),
    /**
     * Errors are sticky, and they can carry the way out.
     *
     * Sticky because something failed and the reason must not disappear
     * before it is read. The ACTION is the half that was missing: a warning
     * could offer one and an error could not, which is backwards — a warning
     * is a fact, and an error is the thing somebody is stuck on. Twenty-two
     * of these said "Could not X" over a raw server sentence and stopped
     * there, leaving a person with a failure and no next move.
     */
    error: (title: string, text?: string, action?: Toast['action']) =>
      push({
        kind: 'error',
        title,
        ...(text ? { text } : {}),
        ...(action ? { action } : {}),
        sticky: true,
      }),
  }
}

export function Toasts({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: number) => void
}) {
  return (
    <div className="toasts" role="region" aria-label="Notifications" aria-live="polite">
      {toasts.map((t) => (
        <ToastRow key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastRow({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  useEffect(() => {
    if (toast.sticky) return
    const timer = window.setTimeout(() => onDismiss(toast.id), 4200)
    return () => window.clearTimeout(timer)
    // `seen` as well as the id: a repeat that only bumps the count must
    // restart the clock, or the fourth failure in five seconds is dismissed
    // by a timer the first one started.
  }, [toast.id, toast.seen, toast.sticky, onDismiss])

  return (
    /**
     * A failure interrupts; everything else waits its turn.
     *
     * The region is `aria-live="polite"`, which is right for "Saved" and
     * wrong for the one kind that is sticky BECAUSE the person has to read it:
     * a polite announcement waits for the reader to stop, and a failed
     * publish can sit unspoken behind whatever they are typing. `role="alert"`
     * on the error row alone says the loud thing loudly and leaves the rest
     * as it was.
     */
    <div
      className={`toast toast--${toast.kind}`}
      {...(toast.kind === 'error' && { role: 'alert' })}
    >
      <Icon name={ICON[toast.kind]} size={15} />
      <div className="toast__body">
        <div className="toast__title">
          {toast.title}
          {(toast.seen ?? 1) > 1 && (
            <span className="toast__seen" aria-label={`${toast.seen} times`}>
              &times;{toast.seen}
            </span>
          )}
        </div>
        {toast.text && <div className="toast__text">{toast.text}</div>}
        {toast.action &&
          (toast.action.href ? (
            <a className="toast__action" href={toast.action.href} target="_blank" rel="noreferrer">
              {toast.action.label}
            </a>
          ) : (
            <button className="toast__action" onClick={toast.action.onClick}>
              {toast.action.label}
            </button>
          ))}
      </div>
      <button className="btn btn--ghost btn--icon btn--sm" onClick={() => onDismiss(toast.id)}>
        <Icon name="close" size={13} label="Dismiss" />
      </button>
    </div>
  )
}
